import type { ViewComponent } from "@frui.ts/views";
import { registerView } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import type { ListColumnDefinition } from "views/common/list";
import ListView from "views/common/list";
import React from "react";
import type OutgoingPayGatePaymentResponse from "entities/outgoingPayGatePaymentResponse";
import { formatAmount, formatDate, formatDateTime, formatLicensePlate } from "utils/localizations";
import CardGatewayViewModel from "viewModels/finance/outgoingPayments/cardGatewayViewModel";
import Button from "@edaz/edazkit/Button";
import CardGatewayFilters from "./cardGatewayFilters";
import { emptyValue } from "utils/emptyValue";

const CardGatewayView: ViewComponent<CardGatewayViewModel> = observer(({ vm }) => {
  const onItemClick = vm.onItemClick();

  const columns: ListColumnDefinition<OutgoingPayGatePaymentResponse>[] = [
    {
      title: vm.translate("licensePlate"),
      valueFormatter: props => {
        const vehicle = props.item.searchedCharge?.vehicle;
        return vehicle ? formatLicensePlate(vehicle) : emptyValue;
      },
    },
    {
      property: "authorizedDate",
      title: vm.translateAttribute("authorizedDate"),
      valueFormatter: props => formatDateTime(props.value),
    },
    {
      property: "exportedDate",
      title: vm.translateAttribute("exportedDate"),
      valueFormatter: props => formatDate(props.value),
    },
    {
      property: "variableSymbol",
      title: vm.translate("variableSymbolOutgoingPayment"),
      valueFormatter: props => props.value || emptyValue,
    },
    {
      property: "paidAmount",
      title: vm.translateAttribute("paidAmount"),
      valueFormatter: props => formatAmount(props.value),
    },
    {
      property: "cardNumber",
      title: vm.translateAttribute("cardNumber"),
      valueFormatter: props => props.value ?? emptyValue,
      numberProperty: true,
    },
    {
      title: vm.translate("officialRecordId"),
      valueFormatter: ({ item }) => item.searchedCharge?.officialRecordId ?? emptyValue,
    },
    {
      property: "arn",
      title: vm.translateAttribute("arn"),
      valueFormatter: props => props.value ?? emptyValue,
      numberProperty: true,
    },
    {
      property: "status",
      title: vm.translateAttribute("status"),
      valueFormatter: props => vm.translateOutgoingPayGatePaymentStatus(props.value),
    },
    {
      property: "refundReason",
      title: vm.translateAttribute("refundReason"),
      valueFormatter: props => props.value || emptyValue,
    },
    {
      title: vm.translate("chargeOrderId"),
      valueFormatter: ({ item }) => item.searchedCharge?.chargeOrderId ?? emptyValue,
    },
    {
      title: vm.translate("chargeId"),
      valueFormatter: ({ item }) => item.searchedCharge?.chargeId ?? emptyValue,
    },
  ];

  let actionButtons;
  if (onItemClick) {
    actionButtons = (item: OutgoingPayGatePaymentResponse) => (
      <Button size="sm" className="rounded btn btn-light btn-sm" onClick={() => onItemClick(item)}>
        {vm.translate("detail")}
      </Button>
    );
  }

  return (
    <>
      <CardGatewayFilters vm={vm} />
      <ListView
        showDetailWithList={true}
        vm={vm}
        columns={columns}
        hideTitle={true}
        mandatoryFilter={true}
        actionButtons={actionButtons}
      />
    </>
  );
});

registerView(CardGatewayView, CardGatewayViewModel);
